<script setup>
import { reactive, computed, watch, ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';

import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Editor from 'dashboard/components-next/Editor/Editor.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import CustomToolsAPI from 'dashboard/api/captain/customTools';

const props = defineProps({
  assistant: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['submit']);

const { t } = useI18n();

const initialState = {
  name: '',
  description: '',
  productName: '',
  preferredBrand: '',
  productRecommendationLimit: 3,
  commerceToneMode: 'calido',
  channelFormatPreference: 'auto',
  purchaseCompletionToolId: 'vtex',
  purchaseCompletionInstructions: '',
  purchaseButtonText: 'Continuar compra',
  features: {
    conversationFaqs: false,
    memories: false,
    citations: false,
    contactAttributes: false,
  },
};

const state = reactive({ ...initialState });

const validationRules = {
  name: { required, minLength: minLength(1) },
  description: { required, minLength: minLength(1) },
  productName: { required, minLength: minLength(1) },
};

const v$ = useVuelidate(validationRules, state);

const getErrorMessage = field => {
  return v$.value[field].$error ? v$.value[field].$errors[0].$message : '';
};

const formErrors = computed(() => ({
  name: getErrorMessage('name'),
  description: getErrorMessage('description'),
  productName: getErrorMessage('productName'),
}));

const customToolOptions = ref([]);

const purchaseCompletionOptions = computed(() => [
  {
    value: 'vtex',
    label: t('CAPTAIN.ASSISTANTS.FORM.PURCHASE_COMPLETION_TOOL.OPTIONS.VTEX'),
  },
  {
    value: 'nuvei',
    label: t('CAPTAIN.ASSISTANTS.FORM.PURCHASE_COMPLETION_TOOL.OPTIONS.NUVEI'),
  },
  ...customToolOptions.value.map(tool => ({
    value: tool.slug,
    label: tool.title,
  })),
]);

const usesCustomPurchaseTool = computed(
  () => !['vtex', 'nuvei'].includes(state.purchaseCompletionToolId)
);

const loadCustomTools = async () => {
  try {
    const { data } = await CustomToolsAPI.get();
    customToolOptions.value = Array(data.payload || []).filter(
      tool => tool.enabled || tool.slug === state.purchaseCompletionToolId
    );
  } catch {
    customToolOptions.value = [];
  }
};

const updateStateFromAssistant = assistant => {
  const { config = {} } = assistant;
  state.name = assistant.name;
  state.description = assistant.description;
  state.productName = config.product_name;
  state.preferredBrand = config.preferred_brand || '';
  state.productRecommendationLimit = config.product_recommendation_limit ?? 3;
  state.commerceToneMode = config.commerce_tone_mode || 'calido';
  state.channelFormatPreference = config.channel_format_preference || 'auto';
  state.purchaseCompletionToolId = config.purchase_completion_tool_id || 'vtex';
  state.purchaseCompletionInstructions =
    config.purchase_completion_instructions || '';
  state.purchaseButtonText = config.purchase_button_text || 'Continuar compra';
  state.features = {
    conversationFaqs: config.feature_faq || false,
    memories: config.feature_memory || false,
    citations: config.feature_citation || false,
    contactAttributes: config.feature_contact_attributes || false,
  };
};

const handleBasicInfoUpdate = async () => {
  const result = await Promise.all([
    v$.value.name.$validate(),
    v$.value.description.$validate(),
    v$.value.productName.$validate(),
  ]).then(results => results.every(Boolean));
  if (!result) return;

  const payload = {
    name: state.name,
    description: state.description,
    config: {
      ...props.assistant.config,
      product_name: state.productName,
      preferred_brand: state.preferredBrand,
      product_recommendation_limit: state.productRecommendationLimit ?? 3,
      commerce_tone_mode: state.commerceToneMode,
      channel_format_preference: state.channelFormatPreference,
      purchase_completion_tool_id: state.purchaseCompletionToolId || 'vtex',
      purchase_completion_instructions: state.purchaseCompletionInstructions,
      purchase_button_text: state.purchaseButtonText || 'Continuar compra',
      feature_faq: state.features.conversationFaqs,
      feature_memory: state.features.memories,
      feature_citation: state.features.citations,
      feature_contact_attributes: state.features.contactAttributes,
    },
  };

  emit('submit', payload);
};

watch(
  () => props.assistant,
  newAssistant => {
    if (newAssistant) updateStateFromAssistant(newAssistant);
  },
  { immediate: true }
);

onMounted(loadCustomTools);
</script>

<template>
  <div class="flex flex-col gap-6">
    <Input
      v-model="state.name"
      :label="t('CAPTAIN.ASSISTANTS.FORM.NAME.LABEL')"
      :placeholder="t('CAPTAIN.ASSISTANTS.FORM.NAME.PLACEHOLDER')"
      :message="formErrors.name"
      :message-type="formErrors.name ? 'error' : 'info'"
    />

    <Input
      v-model="state.productName"
      :label="t('CAPTAIN.ASSISTANTS.FORM.PRODUCT_NAME.LABEL')"
      :placeholder="t('CAPTAIN.ASSISTANTS.FORM.PRODUCT_NAME.PLACEHOLDER')"
      :message="formErrors.productName"
      :message-type="formErrors.productName ? 'error' : 'info'"
    />

    <Input
      v-model="state.preferredBrand"
      :label="t('CAPTAIN.ASSISTANTS.FORM.PREFERRED_BRAND.LABEL')"
      :placeholder="t('CAPTAIN.ASSISTANTS.FORM.PREFERRED_BRAND.PLACEHOLDER')"
      :message="t('CAPTAIN.ASSISTANTS.FORM.PREFERRED_BRAND.DESCRIPTION')"
    />

    <Input
      v-model="state.productRecommendationLimit"
      type="number"
      min="1"
      max="10"
      :label="t('CAPTAIN.ASSISTANTS.FORM.PRODUCT_RECOMMENDATION_LIMIT.LABEL')"
      :placeholder="
        t('CAPTAIN.ASSISTANTS.FORM.PRODUCT_RECOMMENDATION_LIMIT.PLACEHOLDER')
      "
      :message="
        t('CAPTAIN.ASSISTANTS.FORM.PRODUCT_RECOMMENDATION_LIMIT.DESCRIPTION')
      "
    />

    <div class="flex flex-col gap-2">
      <label class="text-sm font-medium text-n-slate-12">
        {{ t('CAPTAIN.ASSISTANTS.FORM.COMMERCE_TONE_MODE.LABEL') }}
      </label>
      <select
        v-model="state.commerceToneMode"
        class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-alpha-2 text-sm text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand"
      >
        <option value="formal">
          {{ t('CAPTAIN.ASSISTANTS.FORM.COMMERCE_TONE_MODE.OPTIONS.FORMAL') }}
        </option>
        <option value="calido">
          {{ t('CAPTAIN.ASSISTANTS.FORM.COMMERCE_TONE_MODE.OPTIONS.CALIDO') }}
        </option>
        <option value="rapido">
          {{ t('CAPTAIN.ASSISTANTS.FORM.COMMERCE_TONE_MODE.OPTIONS.RAPIDO') }}
        </option>
      </select>
      <span class="text-sm text-n-slate-11">
        {{ t('CAPTAIN.ASSISTANTS.FORM.COMMERCE_TONE_MODE.DESCRIPTION') }}
      </span>
    </div>

    <div class="flex flex-col gap-2">
      <label class="text-sm font-medium text-n-slate-12">
        {{ t('CAPTAIN.ASSISTANTS.FORM.CHANNEL_FORMAT_PREFERENCE.LABEL') }}
      </label>
      <select
        v-model="state.channelFormatPreference"
        class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-alpha-2 text-sm text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand"
      >
        <option value="auto">
          {{
            t('CAPTAIN.ASSISTANTS.FORM.CHANNEL_FORMAT_PREFERENCE.OPTIONS.AUTO')
          }}
        </option>
        <option value="interactive_first">
          {{
            t(
              'CAPTAIN.ASSISTANTS.FORM.CHANNEL_FORMAT_PREFERENCE.OPTIONS.INTERACTIVE_FIRST'
            )
          }}
        </option>
        <option value="text_first">
          {{
            t(
              'CAPTAIN.ASSISTANTS.FORM.CHANNEL_FORMAT_PREFERENCE.OPTIONS.TEXT_FIRST'
            )
          }}
        </option>
      </select>
      <span class="text-sm text-n-slate-11">
        {{ t('CAPTAIN.ASSISTANTS.FORM.CHANNEL_FORMAT_PREFERENCE.DESCRIPTION') }}
      </span>
    </div>

    <div class="flex flex-col gap-2">
      <label class="text-sm font-medium text-n-slate-12">
        {{ t('CAPTAIN.ASSISTANTS.FORM.PURCHASE_COMPLETION_TOOL.LABEL') }}
      </label>
      <select
        v-model="state.purchaseCompletionToolId"
        class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-alpha-2 text-sm text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand"
      >
        <option
          v-for="option in purchaseCompletionOptions"
          :key="option.value"
          :value="option.value"
        >
          {{ option.label }}
        </option>
      </select>
      <span class="text-sm text-n-slate-11">
        {{ t('CAPTAIN.ASSISTANTS.FORM.PURCHASE_COMPLETION_TOOL.DESCRIPTION') }}
      </span>
    </div>

    <TextArea
      v-if="usesCustomPurchaseTool"
      v-model="state.purchaseCompletionInstructions"
      :label="
        t('CAPTAIN.ASSISTANTS.FORM.PURCHASE_COMPLETION_INSTRUCTIONS.LABEL')
      "
      :placeholder="
        t(
          'CAPTAIN.ASSISTANTS.FORM.PURCHASE_COMPLETION_INSTRUCTIONS.PLACEHOLDER'
        )
      "
      :message="
        t(
          'CAPTAIN.ASSISTANTS.FORM.PURCHASE_COMPLETION_INSTRUCTIONS.DESCRIPTION'
        )
      "
      :rows="4"
    />

    <Input
      v-model="state.purchaseButtonText"
      :label="t('CAPTAIN.ASSISTANTS.FORM.PURCHASE_BUTTON_TEXT.LABEL')"
      :placeholder="
        t('CAPTAIN.ASSISTANTS.FORM.PURCHASE_BUTTON_TEXT.PLACEHOLDER')
      "
      :message="t('CAPTAIN.ASSISTANTS.FORM.PURCHASE_BUTTON_TEXT.DESCRIPTION')"
    />

    <Editor
      v-model="state.description"
      :label="t('CAPTAIN.ASSISTANTS.FORM.DESCRIPTION.LABEL')"
      :placeholder="t('CAPTAIN.ASSISTANTS.FORM.DESCRIPTION.PLACEHOLDER')"
      :message="formErrors.description"
      :message-type="formErrors.description ? 'error' : 'info'"
      class="z-0"
    />

    <div class="flex flex-col gap-2">
      <label class="text-sm font-medium text-n-slate-12">
        {{ t('CAPTAIN.ASSISTANTS.FORM.FEATURES.TITLE') }}
      </label>
      <div class="flex flex-col gap-2">
        <label class="flex items-center gap-2">
          <input v-model="state.features.conversationFaqs" type="checkbox" />
          {{ t('CAPTAIN.ASSISTANTS.FORM.FEATURES.ALLOW_CONVERSATION_FAQS') }}
        </label>
        <label class="flex items-center gap-2">
          <input v-model="state.features.memories" type="checkbox" />
          {{ t('CAPTAIN.ASSISTANTS.FORM.FEATURES.ALLOW_MEMORIES') }}
        </label>
        <label class="flex items-center gap-2">
          <input v-model="state.features.citations" type="checkbox" />
          {{ t('CAPTAIN.ASSISTANTS.FORM.FEATURES.ALLOW_CITATIONS') }}
        </label>
        <label class="flex items-center gap-2">
          <input v-model="state.features.contactAttributes" type="checkbox" />
          {{ t('CAPTAIN.ASSISTANTS.FORM.FEATURES.ALLOW_CONTACT_ATTRIBUTES') }}
        </label>
      </div>
    </div>

    <div>
      <Button
        :label="t('CAPTAIN.ASSISTANTS.FORM.UPDATE')"
        @click="handleBasicInfoUpdate"
      />
    </div>
  </div>
</template>
